<template>
  <div
    class="wl-theme"
    :class="theme"
  >
    <div class="wl-theme__header">
      <div class="header-title">
        <div class="header-icon">
          <IconCustom
            :name="theme==='light'?'icn20_sun':'icn20_moon'"
            color="#96A2AE"
          />
        </div>
        <div class="header-label">
          {{ theme === 'light' ? $t('whitelabel.designTab.lightTheme') : $t('whitelabel.designTab.darkTheme') }}
        </div>
      </div>

      <div class="header-switch">
        <div class="switch-label">
          {{ enableTheme[theme] ? $t('whitelabel.designTab.enable') : $t('whitelabel.designTab.disable') }}
        </div>
        <v-switch
          class="pa-0"
          :input-value="enableTheme[theme]"
          color="primary"
          hide-details
          :readonly="theme==='light'&&!enableTheme['dark']"
          @change="enableTheme[theme]=$event"
          @click.stop=""
        />
      </div>
    </div>
    <div class="wl-theme__logo">
      <div
        class="logo-wrapper"
      >
        <template v-if="!themeLogo">
          <div
            class="upload-title"
            @click="$refs.image.click()"
          >
            {{ $t('whitelabel.designTab.logoUpload') }}
          </div>
          <div class="upload-details">
            {{ $t('whitelabel.designTab.logoDetails') }}
          </div>
        </template>
        <v-img
          v-else
          width="100%"
          height="100%"
          contain
          :src="themeLogo"
        />
        <input
          ref="image"
          type="file"
          style="display: none"
          accept="image/*"
          @change="setImage"
        >
      </div>
      <div class="upload-actions">
        <div class="upload-actions__title">
          {{ $t('whitelabel.designTab.companyLogo') }}
          <v-tooltip top>
            <template #activator="{ on }">
              <span
                v-on="on"
              >
                <IconCustom
                  color="#96A2AE"
                  name="icn16_info_circle"
                />
              </span>
            </template>
            {{ $t('whitelabel.designTab.logoHint') }}
          </v-tooltip>
        </div>
        <div class="upload-actions__wrapper">
          <div
            class="upload-actions__load"
            @click="$refs.image.click()"
          >
            {{ $t('whitelabel.designTab.loadLogo') }}
            <div />
          </div>
          <div
            class="upload-actions__delete"
            @click="updateAvatar(null)"
          >
            {{ $t('whitelabel.designTab.deleteLogo') }}
            <div />
          </div>
        </div>
        <helper-cropper
          ref="cropper"
          :aspect-ratio="250 / 100"
          :width="250"
          :height="100"
          @updateAvatar="updateAvatar"
        />
      </div>
    </div>
    <div class="wl-theme__colors">
      <div
        v-for="(c,i) in supported_colors"
        :key="i"
        class="color-wrapper"
      >
        <div class="color-label">
          {{ c.text }}
        </div>
        <div class="color-preview">
          <div
            v-if="!c.isText"
            :id="c.id"
            class="color-preview__square"
            :style="'background-color:'+ getDefaultColor(c.id)"
            @click="openColorDialog"
          />
          <div
            v-else
            :id="c.id"
            class="color-preview__text-square"
            :style="{color: getDefaultColor(c.id),backgroundColor: getDefaultColor('primaryColor')}"
            @click="openColorDialog"
          >
            A
          </div>
          <div
            class="color-preview__label"
          >
            {{ getDefaultColor(c.id) }}
          </div>
        </div>
      </div>
      <chrome-picker
        v-if="colorDialog"
        v-on-clickaway="hideColorDialog"
        :value="colors"
        @input="updateColor"
      />
    </div>
    <div class="wl-theme__mockup">
      <WhiteLabelMockup
        :theme="theme"
        :favicon="favicon"
        :logo="themeLogo"
        :title="(editForm && editForm.name) || ''"
        :address="domain"
      />
    </div>
  </div>
</template>

<script>

import {directive as onClickaway} from "vue-clickaway";
import {Chrome} from "vue-color";
import WhiteLabelMockup from "@/views/WhiteLabel/partial/WhiteLabelMockup.vue";
import IconCustom from "@/components/helper/IconCustom.vue";
import HelperCropper from "@/components/helper/Cropper.vue";

export default {
  name: "WhiteLabelTheme",
  directives: {
    onClickaway
  },
  components: {
    HelperCropper,
    IconCustom,
    WhiteLabelMockup,
    "chrome-picker": Chrome,
  },
  props: {
    editForm: {
      type: Object,
      default: null
    },
    theme: {
      type: String,
      default: 'light'
    },
    enableTheme: {
      type: Object,
      required: true,
    },
    favicon: {
      type: [String, null],
      default: null
    },
    fontFamily: {
      type: String,
      default: 'Open Sans'
    }
  },
  data() {
    return {
      supported_colors: [
        {id: "primaryColor", text: this.$t('whitelabel.designTab.primaryColor')},
        {id: "secondaryColor", text: this.$t('whitelabel.designTab.secondaryColor')},
        {id: "primaryTextColor", text: this.$t('whitelabel.designTab.primaryTextColor'), isText: true},
      ],
      dialog: false,
      colorDialog: false,
      current_color_selection: null,
      imageName: "",
      imageFile: "",
      valid: false,
      themeLogo: null,
      colors: {
        rgba: {r: 255, g: 255, b: 255, a: 1},
      },
      primaryColor: {
        rgba: {r: 0, g: 89, b: 255, a: 1},
      },
      primaryTextColor: {
        rgba: {r: 255, g: 255, b: 255, a: 1},
      },
      secondaryColor: {
        rgba: {r: 150, g: 162, b: 174, a: 1},
      },
    };
  },
  computed: {
    mockupTextStyle() {
      return {
        color: this.convertRGBAtoString(this.primaryTextColor.rgba),
        fontFamily: this.fontFamily
      }
    },
    mockupSecondaryTextStyle() {
      return {
        color: "#000000",
        fontFamily: this.fontFamily
      }
    },
    domain() {
      if(this.editForm) {
        return this.editForm.domain || `${this.editForm.subdomain}.${this.$ls.get("config").domain}`
      }
      return ''
    },
  },
  watch: {
    editForm(value) {
      if (value && value.design) {
        if (this.theme === 'light') {
          this.themeLogo = value.design.light_theme_logo;
          this.primaryColor.rgba = this.convertStringToRGBA(value.design.light_theme_primary_color);
          this.primaryTextColor.rgba = this.convertStringToRGBA(value.design.light_theme_primary_text_color);
          this.secondaryColor.rgba = this.convertStringToRGBA(value.design.light_theme_secondary_color);
        }
        if (this.theme === 'dark') {
          this.themeLogo = value.design.dark_theme_logo;
          this.primaryColor.rgba = this.convertStringToRGBA(value.design.dark_theme_primary_color);
          this.primaryTextColor.rgba = this.convertStringToRGBA(value.design.dark_theme_primary_text_color);
          this.secondaryColor.rgba = this.convertStringToRGBA(value.design.dark_theme_secondary_color);
        }
      }
    }
  },
  methods: {
    save() {
      let form = {
        ...(this.theme === 'light' ? {
          light_theme_logo: this.themeLogo,
          light_theme_primary_color: this.convertRGBAtoString(this.primaryColor.rgba),
          light_theme_primary_text_color: this.convertRGBAtoString(this.primaryTextColor.rgba),
          light_theme_secondary_color: this.convertRGBAtoString(this.secondaryColor.rgba)
        } : {
          dark_theme_logo: this.themeLogo,
          dark_theme_primary_color: this.convertRGBAtoString(this.primaryColor.rgba),
          dark_theme_primary_text_color: this.convertRGBAtoString(this.primaryTextColor.rgba),
          dark_theme_secondary_color: this.convertRGBAtoString(this.secondaryColor.rgba)
        }),
      }
      this.$emit('saveThemes', form, this.theme);
    },
    setImage(e) {
      if (!this.$refs.cropper.setImage(e)) {
        this.$refs.image.value = "";
      }
    },
    convertBase64(file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
          resolve(fileReader.result);
        };

        fileReader.onerror = (error) => {
          reject(error);
        };
      });
    },
    updateAvatar(avatar) {
      this.themeLogo = avatar;
    },
    updateColor(color) {
      this[this.current_color_selection].rgba = color.rgba;
      this.setCSSVar(this.current_color_selection, this.convertRGBAtoString(color.rgba));
    },
    setCSSVar(name, color) {
      let formattedName;
      if (name === 'primaryColor') {
        formattedName = `--${this.theme}-preview-theme-primary-color`;
      }
      if (name === 'secondaryColor') {
        formattedName = `--${this.theme}-preview-theme-secondary-color`;
      }
      if (name === 'primaryTextColor') {
        formattedName = `--${this.theme}-preview-theme-primary-text-color`;
      }
      const root = document.querySelector(":root");
      root.style.setProperty(formattedName, color);
    },
    getDefaultColor(id) {
      return this.convertRGBAtoString(this[id].rgba);
    },
    openColorDialog(e) {
      this.colors.rgba = this[e.target.id].rgba
      this.current_color_selection = e.target.id
      this.colorDialog = true
    },
    hideColorDialog(e) {
      if (e) e.preventDefault();
      if (this.colorDialog && e.target.className !== "selected-color") {
        this.colorDialog = false;
      }
    },
    convertRGBAtoString(val) {
      if (val.hasOwnProperty('r') &&
          val.hasOwnProperty('g') &&
          val.hasOwnProperty('b') &&
          val.hasOwnProperty('a')) {

        return `rgba(${val.r},${val.g},${val.b},${val.a})`;
      }
      return val;
    },
    convertStringToRGBA(val) {
      const rgbaRegex = /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/;
      const match = val.match(rgbaRegex);
      if (match) {
        const [, red, green, blue, alpha] = match;
        return {
          r: parseInt(red),
          g: parseInt(green),
          b: parseInt(blue),
          a: parseFloat(alpha)
        }
      }
      return val;
    }
  }
};
</script>

<style lang="scss" scoped>
figcaption {
  small {
    font-size: 80% !important;
  }
}

.wl-theme {
  max-width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 24px;
  gap: 24px;
  border-radius: 8px;
  border: 1px solid var(--light-theme-surface-color-10, rgba(49, 57, 44, 0.10));
  color: var(--light-theme-surface-color, #2C2D39);
  &.dark {
    border: unset;
    background: var(--dark-theme-surface-background, #2C2D39);
    color: var(--dark-theme-surface-color, #FFF);
    .upload-title {
      color: var(--primary-color, #0059FF);
    }
    .upload-details {
      color: var(--dark-theme-surface-color-30, rgba(255, 255, 255, 0.30)) !important;
    }
    .upload-actions__load {
      color: var(--primary-color, #0059FF);
    }
    .upload-actions__delete {
      color: var(--dark-theme-surface-color-70, rgba(255, 255, 255, 0.70)) !important;
    }
    .logo-wrapper {
      border: 1px solid var(--dark-theme-surface-color-10, rgba(255, 255, 255, 0.10)) !important;
    }
    .header-icon {
      background: var(--dark-theme-surface-color-03, rgba(255, 255, 255, 0.03)) !important;
    }
  }

  .wl-theme__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 24px;
    .header-title{
      display: flex;
      gap: 12px;
      align-items: center;
    }
    .header-icon {
      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      background: var(--light-theme-surface-color-03, rgba(49, 57, 44, 0.03));
    }
    .header-label {
      font-size: 20px;
      font-weight: 600;
    }
    .header-switch {
      display: flex;
      gap: 12px;
      align-items: center;
      justify-content: flex-end;
    }
    .switch-label {
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
    }


  }

  .wl-theme__logo {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;

    .logo-wrapper {
      width: 200px;
      height: 80px;
      padding: 10px;
      border-radius: 4px;
      border: 1px solid var(--light-theme-surface-color-10, rgba(49, 57, 44, 0.10));
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      .upload-title {
        cursor: pointer;
        color: var(--primary-color, #0059FF);
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }

      .upload-details {
        color: var(--light-theme-surface-color-30, rgba(0, 0, 0, 0.30));
        font-size: 10px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
      }
    }

    .upload-actions {
      display: flex;
      flex-direction: column;
      gap: 4px;
      justify-content: center;
      align-items: flex-start;

      &__title {
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        height: 18px;
        line-height: 0;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      &__wrapper {
        display: flex;
        gap: 12px;

        .upload-actions__load {
          cursor: pointer;
          color: var(--primary-color, #0059FF);
          font-size: 13px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
        }

        .upload-actions__delete {
          cursor: pointer;
          color: var(--light-theme-surface-color-70, rgba(49, 57, 44, 0.70));
          font-size: 13px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
        }
      }

    }
  }

  .wl-theme__colors {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 24px;

    .color-wrapper {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .color-label {
        font-size: 11px;
        font-style: normal;
        font-weight: 500;
      }

      .color-preview {
        display: flex;
        gap: 4px;

        .color-preview__square, .color-preview__text-square {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 20px;
          font-weight: 700 !important;
        }
        .color-preview__label {
          height: 32px;
          padding: 5px 9px 5px 12px;
          display: flex;
          display: none;
          justify-content: flex-start;
          align-items: center;
          border-radius: 4px;
          border: 1px solid var(--light-theme-surface-color-10, rgba(49, 57, 44, 0.10));
          font-size: 11px;
          font-style: normal;
          font-weight: 400;
        }
      }
    }

    .colors {
      .selected-color {
        float: left;
        width: 40px;
        height: 40px;
        border: 1px solid #e9e9e9;
        box-shadow: 0 1px 4px 0 rgb(0 0 0 / 10%);
        cursor: pointer;
        border-radius: 3px;
        overflow: hidden;
      }

      small {
        float: left;
        margin: 10px 5px;
      }
    }
  }

  .vc-chrome {
    position: absolute;
    z-index: 100;
    top: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(20px);
  }
}
</style>
